import React from 'react';
import { Button, Tooltip } from 'antd';
import {
	EditOutlined,
	DeleteOutlined,
	EnvironmentOutlined,
	ExportOutlined,
	HistoryOutlined,
	ToolOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ContactAvatarWithNameEmail } from '../contact_avatar_with_name_email/ContactAvatarWithNameEmail';
import ApprovalHierarchyDisplayEntityRequest from '../approval_hierarchy_display_entity_request/ApprovalHierarchyDisplayEntityRequest';
import EntityAddRequestStatusDisplay from '../entity_add_request_status_display/EntityAddRequestStatusDisplay';
import { ENTITY_ADD_REQUEST_STATUSES } from '../../constants/EntityAddRequestStatuses';
import { EDITABLE_ENTITY_ADD_REQUEST_STATUSES } from '../../types/entity_add_requests';
import { canReviewEntityRequest } from '../../utils/EntityRequestApprovalUtils';

// Keeps Review and View the same size so the row actions line up down the column regardless of which
// action a request's status offers.
const PRIMARY_ACTION_WIDTH = 104;

// Wide enough for the whole coverage line ("All Locations & Problem Types") to sit on one row under
// the company name.
const SUPPLIER_INFO_COLUMN_WIDTH = 300;

const formatRequestedDate = (dateString) =>
	dateString ? moment(dateString).format('MMM D, h:mma') : '—';

const summarizeLocations = (payload) => {
	if (nullSafeGet('allLocations', payload)) {
		return 'All Locations';
	}
	const count = nullSafeGetOrElse('locationIds.length', payload, 0);
	return `${count} Location${count === 1 ? '' : 's'}`;
};

const summarizeProblemTypes = (payload) => {
	if (nullSafeGet('allProblemTypes', payload)) {
		return 'All Problem Types';
	}
	const count = nullSafeGetOrElse('problemTypeIds.length', payload, 0);
	return `${count} Problem Type${count === 1 ? '' : 's'}`;
};

/**
 * The requested coverage in one line: a request for everything collapses to a single phrase, and
 * anything narrower is shown per dimension against its own icon — a pin for locations, a wrench for
 * problem types — so a selective request stays readable at column width. The spelled-out counts are on
 * the tooltip, since the compact form leans on the icons to say what each number counts.
 */
const CoverageSummary = ({ payload }) => {
	const allLocations = nullSafeGet('allLocations', payload);
	const allProblemTypes = nullSafeGet('allProblemTypes', payload);
	if (allLocations && allProblemTypes) {
		return (
			<span className="footnoteregular whitespace-nowrap text-character-secondary">
				All Locations &amp; Problem Types
			</span>
		);
	}
	return (
		<Tooltip title={`${summarizeLocations(payload)} · ${summarizeProblemTypes(payload)}`}>
			<span
				className="footnoteregular flex flex-row items-center whitespace-nowrap text-character-secondary"
				style={{ gap: '12px' }}
			>
				<span className="flex flex-row items-center" style={{ gap: '4px' }}>
					<EnvironmentOutlined />
					{allLocations ? 'All Locations' : nullSafeGetOrElse('locationIds.length', payload, 0)}
				</span>
				<span className="flex flex-row items-center" style={{ gap: '4px' }}>
					<ToolOutlined />
					{allProblemTypes
						? 'All Problem Types'
						: nullSafeGetOrElse('problemTypeIds.length', payload, 0)}
				</span>
			</span>
		</Tooltip>
	);
};

interface ColumnHandlers {
	canApprove: boolean;
	currentUserEmail: string;
	onReview: (record: any) => void;
	onEdit: (record: any) => void;
	onDelete: (record: any) => void;
	onView: (record: any) => void;
	onShowAudits: (record: any) => void;
}

export const getEntityAddRequestColumns = ({
	canApprove,
	currentUserEmail,
	onReview,
	onEdit,
	onDelete,
	onView,
	onShowAudits,
}: ColumnHandlers) => {
	const handleReview = (record) => (event) => {
		event.stopPropagation();
		onReview(record);
	};
	const handleEdit = (record) => (event) => {
		event.stopPropagation();
		onEdit(record);
	};
	const handleDelete = (record) => (event) => {
		event.stopPropagation();
		onDelete(record);
	};
	const handleView = (record) => (event) => {
		event.stopPropagation();
		onView(record);
	};
	const handleShowAudits = (record) => (event) => {
		event.stopPropagation();
		onShowAudits(record);
	};

	const requesterOrApproverColumn = canApprove
		? {
				title: 'Requested by',
				dataIndex: 'requestedByEmail',
				// Show the hydrated contact's avatar + name + email; fall back to the raw email.
				render: (email, record) => (
					<ContactAvatarWithNameEmail
						contact={nullSafeGet('requestedByContact', record)}
						fallbackEmail={email}
					/>
				),
		  }
		: {
				title: 'Requested on',
				dataIndex: 'createdAt',
				render: (text) => formatRequestedDate(text),
		  };

	return [
		{
			title: 'Supplier Contact Info',
			dataIndex: ['payload', 'companyName'],
			width: SUPPLIER_INFO_COLUMN_WIDTH,
			render: (text, record) => {
				const payload = nullSafeGetOrElse('payload', record, {});
				return (
					<div className="flex flex-col">
						<span className="bodyregular text-character-primary">
							{nullSafeGetOrElse(
								'companyName',
								payload,
								nullSafeGetOrElse('displayName', record, '—')
							)}
						</span>
						<CoverageSummary payload={payload} />
					</div>
				);
			},
		},
		{
			title: 'Contact Info',
			dataIndex: ['payload', 'contactEmail'],
			render: (text, record) => {
				const payload = nullSafeGetOrElse('payload', record, {});
				const name = `${nullSafeGetOrElse('contactFirstName', payload, '')} ${nullSafeGetOrElse(
					'contactLastName',
					payload,
					''
				)}`.trim();
				return (
					<div className="flex flex-col">
						<span className="bodyregular text-character-primary">{name || '—'}</span>
						<span className="footnoteregular text-character-secondary">
							{nullSafeGetOrElse('contactEmail', payload, '')}
						</span>
					</div>
				);
			},
		},
		requesterOrApproverColumn,
		{
			title: 'Status',
			dataIndex: 'status',
			render: (status, record) => {
				const isPending = status === ENTITY_ADD_REQUEST_STATUSES.pending_review;
				return (
					<div className="flex flex-col" style={{ gap: '4px' }}>
						<div className="flex flex-row items-center" style={{ gap: '12px' }}>
							<EntityAddRequestStatusDisplay status={status} />
							{/* Who a pending request is waiting on stays behind the link: naming every tier and
							    approver inline crowds the row, and the hover card / modal covers the digging. */}
							{isPending ? (
								<ApprovalHierarchyDisplayEntityRequest record={record} label="Approvers" />
							) : null}
						</div>
						{canApprove && isPending ? (
							<span className="bodyregular text-character-secondary">
								Requested: {formatRequestedDate(nullSafeGet('createdAt', record))}
							</span>
						) : null}
						{status === ENTITY_ADD_REQUEST_STATUSES.rejected &&
						nullSafeGet('rejectionReason', record) ? (
							<span className="footnoteregular italic text-character-secondary">
								“{nullSafeGet('rejectionReason', record)}”
							</span>
						) : null}
					</div>
				);
			},
		},
		{
			title: '',
			dataIndex: 'action',
			align: 'left' as const,
			render: (text, record) => {
				const status = nullSafeGet('status', record);
				// Per record, not per page: with a hierarchy configured the record's own tiers say who may
				// review it, and `canApprove` is only the fallback for requests no hierarchy governs.
				const canReview = canReviewEntityRequest(record, currentUserEmail, canApprove);
				// Request history leads the row so it lines up across every status — a rejected request
				// offers nothing else, and the actions beside it are all the same width.
				const historyButton = (
					<Tooltip title="View request history">
						<Button icon={<HistoryOutlined />} onClick={handleShowAudits(record)} />
					</Tooltip>
				);
				const renderActions = (actions) => (
					<div className="flex flex-row items-center" style={{ gap: '8px' }}>
						{historyButton}
						{actions}
					</div>
				);

				if (canReview && status === ENTITY_ADD_REQUEST_STATUSES.pending_review) {
					return renderActions(
						<Button style={{ width: PRIMARY_ACTION_WIDTH }} onClick={handleReview(record)}>
							Review
						</Button>
					);
				}
				if (status === ENTITY_ADD_REQUEST_STATUSES.approved) {
					// An approved request has a supplier behind it, so View leaves for that supplier's page
					// (in a new tab, hence the icon). Without one — an approval the API recorded before it
					// created the entity — it falls back to the read-only request detail.
					const opensSupplierPage = !!nullSafeGet('createdEntityId', record);
					return renderActions(
						<Button style={{ width: PRIMARY_ACTION_WIDTH }} onClick={handleView(record)}>
							VIEW
							{opensSupplierPage ? <ExportOutlined className="ml-2" /> : null}
						</Button>
					);
				}
				// The list is company-wide, but a requester can only edit/delete their OWN
				// non-approved requests (draft / pending / rejected).
				const isOwnRequest = nullSafeGet('requestedByEmail', record) === currentUserEmail;
				if (!canReview && isOwnRequest && status !== ENTITY_ADD_REQUEST_STATUSES.approved) {
					// A request under review can no longer be edited — only draft / rejected are editable.
					const isEditable = EDITABLE_ENTITY_ADD_REQUEST_STATUSES.includes(status);
					const editButton = (
						<Button icon={<EditOutlined />} disabled={!isEditable} onClick={handleEdit(record)} />
					);
					return renderActions(
						<>
							{isEditable ? (
								editButton
							) : (
								// A disabled Button suppresses hover events, so wrap it in a Tooltip + span
								// (the span ensures the hover still fires) to surface why editing is blocked.
								<Tooltip title="Pending requests can't be edited while they're under review.">
									<span className="inline-flex">{editButton}</span>
								</Tooltip>
							)}
							<Button icon={<DeleteOutlined />} onClick={handleDelete(record)} />
						</>
					);
				}
				return renderActions(null);
			},
		},
	];
};
